import { useEffect, useRef, useState } from 'react';

const storageKey = 'heartland.staging-preview.dismissed.v1';
let dismissedInThisPage = false;

function shouldShowNotice() {
  if (window.location.hostname !== 'heartland.atechstl.com'
    || /^\/(manage|api)/i.test(window.location.pathname)
    || dismissedInThisPage) return false;
  try {
    return window.sessionStorage.getItem(storageKey) !== 'true';
  } catch {
    // Privacy settings must not prevent the visitor from dismissing the notice.
    return true;
  }
}

export function StagingNotice() {
  const [visible, setVisible] = useState(shouldShowNotice);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const continueRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!visible) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    continueRef.current?.focus();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      const returnTarget = previousFocus && previousFocus !== document.body && previousFocus.isConnected
        ? previousFocus
        : document.querySelector<HTMLElement>('.site-header .brand');
      returnTarget?.focus({ preventScroll: true });
    };
  }, [visible]);

  function dismiss() {
    dismissedInThisPage = true;
    try { window.sessionStorage.setItem(storageKey, 'true'); } catch { /* Page-lifetime fallback. */ }
    setVisible(false);
  }

  if (!visible) return null;
  return (
    <dialog
      ref={dialogRef}
      className="staging-notice"
      aria-labelledby="staging-notice-title"
      aria-describedby="staging-notice-description"
      onCancel={(event) => { event.preventDefault(); dismiss(); }}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button');
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}
    >
      <button className="staging-notice-close" type="button" aria-label="Close preview notice" onClick={dismiss}>
        <span aria-hidden="true">×</span>
      </button>
      <span className="staging-notice-logo logo-heartland" role="img" aria-label="Heartland Express">
        <span aria-hidden="true">Heartland<br /><b>Express</b></span>
      </span>
      <p className="staging-notice-eyebrow">Website preview</p>
      <h2 id="staging-notice-title">Under development</h2>
      <p id="staging-notice-description">This website is still being developed. Prices, menus, hours and other information may be incomplete or inaccurate. Please confirm details directly with Heartland before relying on them.</p>
      <button ref={continueRef} className="staging-notice-continue" type="button" onClick={dismiss}>Continue to preview</button>
    </dialog>
  );
}
